// All receipts: a browser over every receipt, 50 at a time. Filters, search
// and sort run on the server (the list route); the view's state is held by
// ReceiptsView, so Back from a receipt returns to the same list.
import { useCallback, useEffect, useRef, useState } from 'react';
import { receiptsCall, downloadReceipts, downloadZip } from './receiptsApi';
import { DATE_PRESETS, MATCH_FILTERS, CONTEXT_FILTERS, SORTS, listQuery, mergePage } from './browseModel';
import ReceiptsTable from './ReceiptsTable';
import SummaryStrip from './SummaryStrip';
import SendResult from './SendResult';
import BackupBanner from './BackupBanner';
import './Receipts.css';

const PEOPLE = [{ key: null, label: 'Everyone' }, { key: 'Marc', label: 'Marc' }, { key: 'Angela', label: 'Angela' }];

export default function AllReceipts({ state, onState, onOpenReceipt, onBack, backLabel = 'Finance' }) {
  const [page, setPage] = useState({ receipts: [], more: false, total: 0, summary: null, backupRefused: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [note, setNote] = useState(null);
  const [q, setQ] = useState(state.q || '');
  const [askDownload, setAskDownload] = useState(false);
  const set = (patch) => onState({ ...state, ...patch });

  // Search waits for typing to pause.
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q === (state.q || '')) return undefined;
    timer.current = setTimeout(() => onState({ ...state, q }), 350);
    return () => clearTimeout(timer.current);
  }, [q, state, onState]);

  const load = useCallback(async (offset = 0, shown = []) => {
    setLoading(true); setErr(null);
    try {
      const res = await receiptsCall('list', listQuery(state, offset));
      const m = mergePage(shown, res);
      setPage({ receipts: m.receipts, more: m.more, total: res.total, summary: res.summary, backupRefused: res.backupRefused || null });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [state]);
  useEffect(() => { setPicked(new Set()); load(0, []); }, [load]);

  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const send = async (ids) => {
    setBusy(true); setSent(null); setNote(null);
    try { setSent(await receiptsCall('send', { ids })); setPicked(new Set()); } catch (e) { setNote({ err: true, text: e.message }); }
    setBusy(false);
  };
  const download = async (ids) => {
    setBusy(true); setNote(null); setAskDownload(false);
    const r = await downloadReceipts(ids);
    setNote({ err: r.failed.length > 0, text: `${r.files} file${r.files === 1 ? '' : 's'} downloading${r.failed.length ? `; could not get ${r.failed.join(', ')}` : ''}.` });
    setBusy(false);
  };
  const zip = async (ids) => {
    setBusy(true); setNote(null); setAskDownload(false);
    try {
      const z = await downloadZip(ids);
      setNote({ err: false, text: `${z.name} downloading: ${z.receipts} receipt${z.receipts === 1 ? '' : 's'}, ${z.files} file${z.files === 1 ? '' : 's'} and index.csv.` });
    } catch (e) { setNote({ err: true, text: e.message }); }
    setBusy(false);
  };
  // One receipt downloads at once; several ask: one zip, or separate files.
  const downloadSelected = () => (picked.size === 1 ? download([...picked]) : setAskDownload(true));
  const sortBy = (key) => set(state.sort === key ? { dir: state.dir === 'desc' ? 'asc' : 'desc' } : { sort: key, dir: key === 'merchant' ? 'asc' : 'desc' });
  const arrow = (key) => (state.sort === key ? (state.dir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <div className="fin-root rc-page rc-wide">
      <button className="rc-back" onClick={onBack}>← {backLabel}</button>
      <div className="rc-head"><div>
        <h2 className="rc-title">All receipts</h2>
        <div className="rc-sub">{DATE_PRESETS.find((p) => p.key === state.preset)?.label}{page.summary ? ` · ${page.total} receipt${page.total === 1 ? '' : 's'}` : ''}</div>
      </div></div>

      <BackupBanner refused={page.backupRefused} />
      <SummaryStrip summary={page.summary} />

      <div className="rc-browse-filters">
        <input className="rc-input rc-search" type="search" placeholder="Search store, item, note, email, RCP number or amount" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
      </div>
      <div className="rc-browse-filters">
        {PEOPLE.map((p) => <button key={p.label} className={`rc-seg${state.person === p.key ? ' rc-seg--on' : ''}`} onClick={() => set({ person: p.key })}>{p.label}</button>)}
        <select className="rc-select" value={state.preset} onChange={(e) => set({ preset: e.target.value })} aria-label="Dates">
          {DATE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {state.preset === 'custom' && (
          <>
            <label>From <input className="rc-input" type="date" value={state.from} onChange={(e) => set({ from: e.target.value })} /></label>
            <label>To <input className="rc-input" type="date" value={state.to} onChange={(e) => set({ to: e.target.value })} /></label>
          </>
        )}
        <select className="rc-select" value={state.context} onChange={(e) => set({ context: e.target.value })} aria-label="Context">
          <option value="">Any context</option>
          {CONTEXT_FILTERS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select className="rc-select" value={state.match} onChange={(e) => set({ match: e.target.value })} aria-label="Chase">
          <option value="">Any Chase state</option>
          {MATCH_FILTERS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <input className="rc-input" style={{ width: 130 }} placeholder="Store" value={state.merchant} onChange={(e) => set({ merchant: e.target.value })} aria-label="Store" />
        <label><input type="checkbox" checked={state.flagged} onChange={(e) => set({ flagged: e.target.checked })} /> Flagged</label>
        <label><input type="checkbox" checked={state.owed} onChange={(e) => set({ owed: e.target.checked })} /> Owed to me</label>
      </div>

      <div className="rc-actions" style={{ alignItems: 'center' }}>
        <span className="rc-note" style={{ margin: 0 }}>Sort:</span>
        {SORTS.map((s) => <button key={s.key} className={`rc-seg${state.sort === s.key ? ' rc-seg--on' : ''}`} onClick={() => sortBy(s.key)}>{s.label}{arrow(s.key)}</button>)}
        <span style={{ flex: 1 }} />
        <button className="rc-btn" disabled={!picked.size || busy} onClick={downloadSelected}>Download selected ({picked.size})</button>
        <button className="rc-btn rc-btn--primary" disabled={!picked.size || busy} onClick={() => send([...picked])}>✉ Send selected ({picked.size})</button>
      </div>
      {askDownload && picked.size > 1 && (
        <div className="rc-panel rc-panel--beige" role="group" aria-label="Download choice">
          <div style={{ fontSize: '0.84rem', marginBottom: 6 }}>Download {picked.size} receipts as</div>
          <div className="rc-actions" style={{ marginBottom: 0 }}>
            <button className="rc-btn rc-btn--primary" disabled={busy} onClick={() => zip([...picked])}>One zip</button>
            <button className="rc-btn" disabled={busy} onClick={() => download([...picked])}>Separate files</button>
            <button className="rc-btn" onClick={() => setAskDownload(false)}>Cancel</button>
          </div>
          <p className="rc-note">The zip holds each original, named date_store_total_number, plus index.csv. Up to 50 receipts or 100 MB.</p>
        </div>
      )}
      {sent && <SendResult result={sent} onClose={() => setSent(null)} />}
      {note && <p className={note.err ? 'rc-error' : 'rc-ok'}>{note.text}</p>}

      {err ? <div className="fin-state fin-state--error"><p>⚠ {err}</p><button className="fin-retry" onClick={() => load(0, [])}>Retry</button></div> : (
        <>
          <ReceiptsTable
            rows={page.receipts}
            picked={picked}
            busy={busy}
            onToggle={toggle}
            onToggleAll={(on) => setPicked(on ? new Set(page.receipts.map((r) => r.id)) : new Set())}
            sortBy={sortBy}
            arrow={arrow}
            onOpen={onOpenReceipt}
            onSend={send}
            onDownload={download}
          />
          {loading && <div className="fin-state" style={{ minHeight: 80 }}><div className="fin-spinner" /></div>}
          {!loading && page.receipts.length === 0 && <p className="rc-note">No receipts match.</p>}
          {!loading && page.more && (
            <div style={{ marginTop: '0.75rem' }}><button className="rc-btn" onClick={() => load(page.receipts.length, page.receipts)}>Load more ({page.total - page.receipts.length} left)</button></div>
          )}
          <p className="rc-note">Showing {page.receipts.length} of {page.total}. Send makes an Outlook draft. Download saves the original; with several ticked, Download selected gives one zip (originals plus index.csv, up to 50 receipts or 100 MB) or separate files.</p>
        </>
      )}
    </div>
  );
}
